import { Component, Mixins, Prop } from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import { BButton, BRow, BCol, BFormGroup, BAlert, BFormRow, BInputGroup, BModal, BvModalEvent } from 'bootstrap-vue'
import { FormModal, NumberMixin, Spinner } from '@kwixeo/interface-vue'
import { KTable } from '@kwixeo/table-vue'
import { KForm, KFormInput, KFormSpinner, KFormSelect, KFormNumber } from '@kwixeo/form-vue'
import Table from '@kwixeo/table-vue/dist/components/Table/Table'
import ProductVariationForm from '../ProductVariationForm/ProductVariationForm.vue'
import StockByLocations from '../StockByLocations/StockByLocations.vue'

@Component({
  computed: {
    ...mapGetters('user', ['currency', 'can']),
    ...mapGetters('subscriptions', ['hasModule'])
  },
  components: {
    FormModal,
    Spinner,
    KTable,
    KForm, KFormInput, KFormSpinner, KFormSelect, KFormNumber,
    BButton, BRow, BCol, BFormGroup, BAlert, BFormRow, BInputGroup, BModal,
    ProductVariationForm,
    StockByLocations
  }
})
export default class ProductVariations extends Mixins(NumberMixin) {
  currency!: KwixeoFramework.Currency
  hasModule!: (key: string) => boolean
  can!: (key: string, level: number) => boolean

  @Prop({ required: true }) value!: KwixeoCatalog.Product

  private variation: Partial<KwixeoCatalog.ProductVariation>|null = null
  private showStock: KwixeoCatalog.ProductVariation|null = null;
  private toDelete: KwixeoCatalog.ProductVariation|null = null
  private deleteIndex: number|null = null
  private deleteLoading = false

  get columns (): KwixeoTable.Columns {
    const product = this.value
    const columns: KwixeoTable.Columns = [
      {
        data: 'level1.value',
        name: product.variation_level1?.label as string
      }
    ]

    if (product.variations_length > 1) {
      columns.push({
        data: 'level2.value',
        name: product.variation_level2?.label as string
      })
    }

    if (product.variations_length > 2) {
      columns.push({
        data: 'level3.value',
        name: product.variation_level3?.label as string
      })
    }

    columns.push(...[
      {
        data: 'reference',
        name: 'Référence'
      },
      {
        data: 'ean_code',
        name: 'Code EAN',
        visible: false
      },
      {
        data: 'provider_reference',
        name: 'Référence fournisseur',
        visible: false
      }
    ])

    if (this.can('sales-margin', 1) || this.can('catalog-products', 3)) {
      columns.push(...[
        {
          data: 'buy_price',
          name: 'Prix achat HT',
          visible: false,
          render: 'currency'
        }
      ])
    }

    columns.push(...[
      {
        data: 'unit_price_ht',
        name: 'Prix U. HT',
        render: 'currency'
      },
      {
        data: 'unit_price_tva',
        name: 'Prix U. TVA',
        visible: false,
        render: 'currency'
      },
      {
        data: 'unit_price_ttc',
        name: 'Prix U. TTC',
        visible: false,
        render: 'currency'
      }
    ])

    if (this.value.with_stock && this.hasModule('stock')) {
      columns.push(...[
        {
          data: 'stock_quantity',
          name: 'Quantité réelle en stock',
          render: 'quantity'
        },
        {
          data: 'stock_theoretical_quantity',
          name: 'Quantité théorique en stock',
          render: 'quantity',
          visible: false
        },
      ])
    }

    columns.push(...[
      {
        data: 'created_at',
        name: 'Date de création',
        visible: false,
        render: 'datetime'
      },
      {
        data: 'updated_at',
        name: 'Dernière modification',
        visible: false,
        render: 'datetime'
      }
    ])

    return columns
  }

  get rowActions (): KwixeoTable.RowActions {
    const actions: KwixeoTable.RowActions = []

    if (this.can('see-stock', 1) && this.hasModule('stock-locations')) {
      actions.push({
        label: 'Stock',
        variant: 'danger',
        click: (variation: KwixeoCatalog.ProductVariation) => {
          this.showStock = variation
        }
      })
    }

    if (this.can('catalog-products', 3)) {
      actions.push({
        icon: 'edit',
        click: (variation: KwixeoCatalog.ProductVariation) => {
          this.variation = variation
        }
      })
    }

    if (this.can('catalog-products', 4)) {
      actions.push({
        icon: 'delete',
        click: (variation: KwixeoCatalog.ProductVariation, index: number) => {
          this.toDelete = variation
          this.deleteIndex = index
        }
      })
    }

    return actions
  }

  private async deleteVariation (event: BvModalEvent): Promise<void> {
    event.preventDefault()
    this.deleteLoading = true

    await this.$kwCatalog.axios.delete('/catalog/product/' + this.value.id + '/variation/' + this.toDelete?.id)
      .then(() => {
        const table = this.$refs.table as Table
        const index = table.getDatas().findIndex(data => data.id === this.toDelete?.id)
        if (index >= 0) {
          table.deleteRow(index)
        }
      })
      .catch(error => {
        throw new Error(error)
      })
      .finally(this.resetDeleting)
  }

  private resetDeleting (): void {
    this.toDelete = null
    this.deleteIndex = null
    this.deleteLoading = false
  }

  private submitComplete (variation: KwixeoCatalog.ProductVariation): void {
    const table = this.$refs.table as Table
    const index = table.getDatas().findIndex(data => data.id === variation.id)
    if (index >= 0) {
      table.updateRow(index, variation)
    } else {
      table.addRow(variation)
    }
  }
}
